import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useDispatch } from 'react-redux';
import { HardwarePlan } from '../../services/oprations/Payment';
import { useTheme } from '../../Context/themeContext';

const PaymentPage = () => {
    const dispatch = useDispatch();
    const { theme } = useTheme();
    
    // State for form fields
    const [formData, setFormData] = useState({
        name: '',
        organizationName: '',
        mobileNumber: '',
        email: '',
        shippingAddress: '',
        numberOfDevices: 1,
    });

    // State for validation errors
    const [errors, setErrors] = useState({
        mobileNumber: '',
        email: ''
    });

    // State to track if payment has been initiated (show QR)
    const [showQR, setShowQR] = useState(false);

    // Theme-aware classes
    const isDark = theme === 'dark';
    
    const bgPrimary = isDark 
        ? 'bg-gradient-to-br from-blue-900 via-gray-900 to-black'
        : 'bg-gradient-to-br from-blue-50 via-gray-50 to-white';
    
    const bgCard = isDark ? 'bg-gray-800/50' : 'bg-white/80';
    const bgInput = isDark ? 'bg-gray-700' : 'bg-white';
    const bgQR = isDark ? 'bg-gray-800/50' : 'bg-white/80';
    const bgPlaceholder = isDark ? 'bg-gray-800/30' : 'bg-gray-100/50';
    
    const borderColor = isDark ? 'border-gray-700' : 'border-gray-200';
    const borderFocus = isDark ? 'focus:ring-blue-500 focus:border-transparent' : 'focus:ring-blue-500 focus:border-blue-500';
    const borderError = isDark ? 'border-red-500' : 'border-red-400';
    const borderInput = isDark ? 'border-gray-600' : 'border-gray-300';
    
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-gray-300' : 'text-gray-700';
    //const textMuted = isDark ? 'text-gray-400' : 'text-gray-500';
    const textLight = isDark ? 'text-gray-400' : 'text-gray-500';
    const textError = isDark ? 'text-red-400' : 'text-red-600';
    const textSuccess = isDark ? 'text-blue-400' : 'text-blue-600';
    
    const placeholderColor = isDark ? 'placeholder-gray-400' : 'placeholder-gray-400';
    const shadowColor = isDark ? 'shadow-xl' : 'shadow-lg';
    
    const buttonPrimary = isDark 
        ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
        : 'bg-blue-500 hover:bg-blue-600 text-white focus:ring-blue-400';
    const buttonDisabled = isDark 
        ? 'bg-gray-600 cursor-not-allowed text-gray-300'
        : 'bg-gray-300 cursor-not-allowed text-gray-500';
    const buttonSecondary = isDark
        ? 'bg-gray-700 hover:bg-gray-600 text-white border-gray-600'
        : 'bg-gray-200 hover:bg-gray-300 text-gray-800 border-gray-300';

    // Phone number validation function
    const validateMobileNumber = (mobileNumber) => {
        const cleanedMobile = mobileNumber.replace(/\D/g, '');
        const mobileRegex = /^[6-9]\d{9}$|^\+\d{1,3}\d{10}$|^\d{10}$/;
        
        if (!mobileNumber) {
            return 'Mobile number is required';
        }
        if (cleanedMobile.length < 10) {
            return 'Please enter a valid mobile number (minimum 10 digits)';
        }
        if (!mobileRegex.test(cleanedMobile) && !mobileRegex.test(mobileNumber)) {
            return 'Please enter a valid mobile number (10 digits or international format)';
        }
        return '';
    };

    // Email validation function
    const validateEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
        if (!email) {
            return 'Email address is required';
        }
        if (!emailRegex.test(email)) {
            return 'Please enter a valid email address (e.g., name@example.com)';
        }
        const domain = email.split('@')[1];
        if (domain && !domain.includes('.com') && !domain.includes('.in') && !domain.includes('.org') && !domain.includes('.net')) {
            return 'Please enter a valid email domain (e.g., gmail.com, yahoo.com)';
        }
        return '';
    };

    // Handle input changes with real-time validation
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'numberOfDevices' ? parseInt(value) || 1 : value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: ''
            }));
        }

        if (name === 'mobileNumber') {
            const mobileError = validateMobileNumber(value);
            setErrors((prev) => ({
                ...prev,
                mobileNumber: mobileError
            }));
        }
        
        if (name === 'email') {
            const emailError = validateEmail(value);
            setErrors((prev) => ({
                ...prev,
                email: emailError
            }));
        }
    };

    // Format mobile number as user types
    const formatMobileNumber = (value) => {
        const cleaned = value.replace(/\D/g, '');
        
        if (cleaned.length === 10) {
            return cleaned.replace(/(\d{5})(\d{5})/, '$1 $2');
        }
        if (cleaned.length === 12 && cleaned.startsWith('91')) {
            const withoutCountry = cleaned.slice(2);
            if (withoutCountry.length === 10) {
                return `+91 ${withoutCountry.replace(/(\d{5})(\d{5})/, '$1 $2')}`;
            }
        }
        if (cleaned.length > 10 && !cleaned.startsWith('91')) {
            const countryCode = cleaned.slice(0, cleaned.length - 10);
            const number = cleaned.slice(-10);
            return `+${countryCode} ${number.replace(/(\d{5})(\d{5})/, '$1 $2')}`;
        }
        return value;
    };

    // Handle mobile number input with formatting
    const handleMobileChange = (e) => {
        const rawValue = e.target.value;
        const formattedValue = formatMobileNumber(rawValue);
        
        setFormData((prev) => ({
            ...prev,
            mobileNumber: formattedValue
        }));

        const mobileError = validateMobileNumber(rawValue);
        setErrors((prev) => ({
            ...prev,
            mobileNumber: mobileError
        }));
    };

    // Handle email change with validation
    const handleEmailChange = (e) => {
        const value = e.target.value;
        
        setFormData((prev) => ({
            ...prev,
            email: value
        }));

        const emailError = validateEmail(value);
        setErrors((prev) => ({
            ...prev,
            email: emailError
        }));
    };

    // Calculate total amount
    const totalAmount = formData.numberOfDevices * 1;
    const formattedAmount = `₹ ${totalAmount.toFixed(2)}`;

    // Handle Pay button click
    const handlePayClick = () => {
        const mobileError = validateMobileNumber(formData.mobileNumber);
        const emailError = validateEmail(formData.email);
        
        const newErrors = {
            mobileNumber: mobileError,
            email: emailError
        };
        
        setErrors(newErrors);
        // eslint-disable-next-line
        const hasErrors = Object.values(newErrors).some(error => error !== '');
        
        if (!formData.name) {
            alert('Please enter your full name');
            return;
        }
        
        if (!formData.shippingAddress) {
            alert('Please enter your shipping address');
            return;
        }
        
        if (mobileError) {
            alert(mobileError);
            return;
        }
        
        if (emailError) {
            alert(emailError);
            return;
        }
        
        const cleanedMobile = formData.mobileNumber.replace(/\D/g, '');
        
        let payload = {
            name: formData.name,
            orgname: formData.organizationName,
            phone: cleanedMobile,
            email: formData.email,
            deviceCount: formData.numberOfDevices,
            shippingAddress: formData.shippingAddress,
            amount: totalAmount
        };
        
        dispatch(HardwarePlan(payload));
        setShowQR(true);
    };

    // Handle back button
    const handleBackClick = () => {
        setShowQR(false);
    };

    // QR Code value
    const qrValue = `upi://pay?pa=7905226299@hdfc&pn=Bandy&Moot Pvt.Ltd&am=${totalAmount}&cu=INR`;

    return (
        <div className={`min-h-screen ${bgPrimary} transition-colors duration-300`}>
            <div className="container px-4 py-20 mx-auto">
                {/* Header */}
                <div className="mb-12 text-center">
                    <h1 className="text-4xl font-bold text-transparent md:text-5xl bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">
                        Checkout
                    </h1>
                    <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Complete your purchase</p>
                </div>

                <div className="flex flex-col max-w-6xl gap-8 mx-auto lg:flex-row">
                    {/* Left Column - Form */}
                    <div className={`flex-1 p-6 border ${borderColor} ${shadowColor} ${bgCard} backdrop-blur-sm rounded-2xl md:p-8 transition-colors duration-300`}>
                        <h2 className={`mb-6 text-2xl font-semibold ${textSuccess}`}>Billing Details</h2>
                        <form className="space-y-5">
                            {/* Name */}
                            <div>
                                <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                    Full Name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 ${textPrimary} ${placeholderColor} ${bgInput} border ${borderInput} rounded-lg focus:outline-none focus:ring-2 ${borderFocus} transition-colors duration-300`}
                                    placeholder="John Doe"
                                    required
                                />
                            </div>

                            {/* Organization Name */}
                            <div>
                                <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                    Organization Name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="organizationName"
                                    value={formData.organizationName}
                                    onChange={handleChange}
                                    className={`w-full px-4 py-3 ${textPrimary} ${placeholderColor} ${bgInput} border ${borderInput} rounded-lg focus:outline-none focus:ring-2 ${borderFocus} transition-colors duration-300`}
                                    placeholder="Acme Inc."
                                />
                            </div>

                            {/* Mobile Number */}
                            <div>
                                <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                    Mobile Number <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="tel"
                                    name="mobileNumber"
                                    value={formData.mobileNumber}
                                    onChange={handleMobileChange}
                                    className={`w-full px-4 py-3 ${bgInput} border rounded-lg focus:outline-none focus:ring-2 ${borderFocus} ${textPrimary} ${placeholderColor} transition-colors duration-300 ${
                                        errors.mobileNumber ? borderError : borderInput
                                    }`}
                                    placeholder="9876543210 or +91 98765 43210"
                                    required
                                />
                                {errors.mobileNumber && (
                                    <p className={`mt-1 text-sm ${textError}`}>{errors.mobileNumber}</p>
                                )}
                                <p className={`mt-1 text-xs ${textLight}`}>
                                    Enter 10-digit Indian number or international format with country code
                                </p>
                            </div>

                            {/* Email */}
                            <div>
                                <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                    Email Address <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleEmailChange}
                                    className={`w-full px-4 py-3 ${bgInput} border rounded-lg focus:outline-none focus:ring-2 ${borderFocus} ${textPrimary} ${placeholderColor} transition-colors duration-300 ${
                                        errors.email ? borderError : borderInput
                                    }`}
                                    placeholder="john@example.com"
                                    required
                                />
                                {errors.email && (
                                    <p className={`mt-1 text-sm ${textError}`}>{errors.email}</p>
                                )}
                                <p className={`mt-1 text-xs ${textLight}`}>
                                    Enter a valid email address (e.g., name@company.com)
                                </p>
                            </div>

                            {/* Shipping Address */}
                            <div>
                                <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                    Shipping Address <span className="text-red-400">*</span>
                                </label>
                                <textarea
                                    name="shippingAddress"
                                    value={formData.shippingAddress}
                                    onChange={handleChange}
                                    rows={3}
                                    className={`w-full px-4 py-3 ${textPrimary} ${placeholderColor} ${bgInput} border ${borderInput} rounded-lg focus:outline-none focus:ring-2 ${borderFocus} transition-colors duration-300`}
                                    placeholder="123 Main St, City, State, ZIP Code, Country"
                                    required
                                />
                            </div>

                            {/* Number of Devices */}
                            <div>
                                <label className={`block mb-1 text-sm font-medium ${textSecondary}`}>
                                    Number of Devices to Purchase
                                </label>
                                <input
                                    type="number"
                                    name="numberOfDevices"
                                    value={formData.numberOfDevices}
                                    onChange={handleChange}
                                    min="1"
                                    max="100"
                                    className={`w-full px-4 py-3 ${textPrimary} ${bgInput} border ${borderInput} rounded-lg focus:outline-none focus:ring-2 ${borderFocus} transition-colors duration-300`}
                                />
                                <p className={`mt-1 text-xs ${textLight}`}>Price per device: ₹1.00</p>
                            </div>

                            {/* Total Amount Display */}
                            <div className={`pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <div className={`flex justify-between text-lg font-semibold ${textPrimary}`}>
                                    <span>Total Amount:</span>
                                    <span className={textSuccess}>{formattedAmount}</span>
                                </div>
                            </div>

                            {/* Pay Button */}
                            <button
                                type="button"
                                onClick={handlePayClick}
                                disabled={!!errors.mobileNumber || !!errors.email}
                                className={`w-full font-bold py-3 px-6 rounded-lg transition duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                    !errors.mobileNumber && !errors.email && formData.name && formData.shippingAddress
                                        ? `${buttonPrimary} focus:ring-offset-${isDark ? 'gray-800' : 'white'}`
                                        : buttonDisabled
                                }`}
                            >
                                Pay {formattedAmount}
                            </button>
                            
                            {(!formData.name || !formData.shippingAddress) && (
                                <p className={`mt-2 text-xs text-center ${isDark ? 'text-yellow-500' : 'text-yellow-600'}`}>
                                    Please fill all required fields to proceed
                                </p>
                            )}
                        </form>
                    </div>

                    {/* Right Column - QR Code */}
                    <div className="flex-1">
                        {showQR ? (
                            <div className={`flex flex-col items-center justify-center p-6 text-center border ${borderColor} ${shadowColor} ${bgQR} backdrop-blur-sm rounded-2xl md:p-8 transition-colors duration-300`}>
                                <h2 className={`mb-4 text-2xl font-semibold ${textSuccess}`}>Scan to Pay</h2>
                                <div className="inline-block p-4 bg-white rounded-xl">
                                    <QRCodeSVG value={qrValue} size={200} bgColor="#ffffff" fgColor="#000000" level="L" includeMargin />
                                </div>
                                <div className="w-full mt-6 space-y-3">
                                    <p className={`text-lg font-bold ${textPrimary}`}>
                                        Pay the amount: <span className={textSuccess}>{formattedAmount}</span>
                                    </p>
                                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                        Scan QR code with any UPI app or banking app
                                    </p>
                                    
                                    {/* Back button */}
                                    <button
                                        onClick={handleBackClick}
                                        className={`px-6 py-2 mt-2 font-medium transition duration-200 border rounded-lg ${buttonSecondary}`}
                                    >
                                        ← Edit Details
                                    </button>
                                    
                                    <p className={`pt-2 text-xs ${textLight}`}>
                                        Once your payment is done, take a screenshot of the payment confirmation and send to:<br />
                                        <span className={`font-medium text-[15px] ${textSuccess}`}>sales@bandy-moot.com</span><br />
                                        and WhatsApp to: <span className={textSuccess}>+91 7905226299</span>
                                    </p>
                                    
                                    {/* Order summary */}
                                    <div className={`pt-4 mt-4 text-left border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                        <p className={`mb-1 text-xs ${textLight}`}>Order Summary:</p>
                                        <p className={`text-sm ${textPrimary}`}>{formData.name}</p>
                                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{formData.email}</p>
                                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{formData.mobileNumber}</p>
                                        <p className={`mt-1 text-xs ${textLight}`}>{formData.shippingAddress}</p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={`${bgPlaceholder} backdrop-blur-sm rounded-2xl ${shadowColor} border ${borderColor} p-8 flex flex-col items-center justify-center text-center h-full min-h-[400px] transition-colors duration-300`}>
                                <div className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                                    <svg className="w-20 h-20 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                    </svg>
                                    <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Complete the form and click "Pay" to see QR code</p>
                                    <p className={`mt-2 text-sm ${textLight}`}>All fields marked with * are required</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentPage;
